import { useAsync, useDebounce } from 'react-use';

import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';

import { kaapApiRef, kaapApiScopes } from '../api/KaapApiClient';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Cluster } from '../types';

export const KAAP_API_TOKEN_SECRET_KEY = 'KAAP_API_TOKEN';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toKaapCluster = (entity: {
  metadata: { name: string };
  spec?: Record<string, unknown>;
}): Cluster | undefined => {
  const kaapData = entity.spec?.kaapData;
  if (!isRecord(kaapData)) {
    return undefined;
  }

  const { id, stage, tier, nodeGroups } = kaapData;
  if (
    typeof id !== 'string' ||
    typeof stage !== 'string' ||
    typeof tier !== 'string'
  ) {
    return undefined;
  }

  return {
    id,
    name: entity.metadata.name,
    stage,
    tier,
    nodeGroups: isRecord(nodeGroups) ? Object.keys(nodeGroups) : [],
  };
};

/**
 * Silently acquires an Azure token scoped to the KaaP API (`${kaap.apiUrl}/.default`)
 * and stores it under `secrets.KAAP_API_TOKEN` so scaffolder steps can use it.
 * Must be called from within the scaffolder's secrets context.
 */
export const useKaapAzureToken = () => {
  const config = useApi(configApiRef);
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const { setSecrets } = useTemplateSecrets();

  const apiUrl = config.getString('kaap.apiUrl');

  useDebounce(async () => {
    const token = await microsoftAuth.getAccessToken(kaapApiScopes(apiUrl));
    if (token) {
      setSecrets({ [KAAP_API_TOKEN_SECRET_KEY]: token });
    }
  }, 1000);
};

export const useGetKaapClusters = () => {
  const catalogApi = useApi(catalogApiRef);
  const { value, loading, error } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'Resource',
        'spec.type': 'kubernetes-cluster',
        'metadata.annotations.tomtom.com/pe-resource-type': 'kaap-cluster',
      },
    });
    return items.flatMap(entity => {
      const cluster = toKaapCluster(entity);
      return cluster ? [cluster] : [];
    });
  }, [catalogApi]);
  return { value: value ?? [], loading, error };
};

export const useGetKaapApplications = () => {
  const kaapApi = useApi(kaapApiRef);
  const { value, loading, error } = useAsync(
    () => kaapApi.getApplications(),
    [kaapApi],
  );
  return { value, loading, error };
};
